import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { usePeriodeStore } from "@/hooks/use-periode";
import { apiClient } from "@/lib/api-client";
import DOMPurify from "dompurify";
import {
  Calendar,
  Clock,
  Loader2,
  MessageSquareWarning,
  Search,
  X,
  FileText,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router";

interface Reply {
  pesan: string;
  role?: string;
  nama?: string;
  created_at: string;
}

interface Pengaduan {
  id: string;
  judul: string;
  isi: string;
  images: string[];
  status: string;
  niu: string;
  nama: string;
  replies: Reply[];
  created_at: string;
  updated_at: string;
}

export default function PertanyaanMasukanPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get("status") || "all",
  );
  const [selectedPertanyaan, setSelectedPertanyaan] =
    useState<Pengaduan | null>(null);
  const [replyText, setReplyText] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [data, setData] = useState<Pengaduan[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [confirmCloseId, setConfirmCloseId] = useState<string | null>(null);

  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1", 10),
  );
  const [limit, setLimit] = useState(
    parseInt(searchParams.get("limit") || "10", 10),
  );
  const [isNext, setIsNext] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(
    searchParams.get("search") || "",
  );

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) next.set("search", debouncedSearch);
        else next.delete("search");

        if (statusFilter !== "all") next.set("status", statusFilter);
        else next.delete("status");

        if (page !== 1) next.set("page", page.toString());
        else next.delete("page");

        if (limit !== 10) next.set("limit", limit.toString());
        else next.delete("limit");

        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, statusFilter, page, limit, setSearchParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const { getYearAndSemester } = usePeriodeStore();
  const { year, semester } = getYearAndSemester();

  useEffect(() => {
    if (year && semester) {
      fetchData();
    }
  }, [year, semester, page, limit, debouncedSearch, statusFilter]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        year: year.toString(),
        semester: semester.toString(),
        page: page.toString(),
        limit: limit.toString(),
      });
      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }

      const resData = await apiClient<any>(
        `${import.meta.env.VITE_API_BASE_URL}/api/v1/admin/pengaduan?${params.toString()}`,
      );
      setData(resData.datas || []);
      if (resData.is_next !== undefined) {
        setIsNext(resData.is_next);
      } else {
        setIsNext((resData.datas || []).length >= limit);
      }
    } catch (err: any) {
      toast.error(err.message || "Terjadi kesalahan");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenReply = (item: Pengaduan) => {
    setSelectedPertanyaan(item);
    setReplyText(""); // Admin usually adds a new reply to the thread
    setIsDialogOpen(true);
  };

  const handleSubmitReply = async () => {
    if (!replyText.trim()) {
      toast.error("Jawaban tidak boleh kosong!");
      return;
    }

    if (!selectedPertanyaan) return;

    setIsSubmitting(true);
    try {
      await apiClient(
        `${import.meta.env.VITE_API_BASE_URL}/api/v1/admin/pengaduan/${selectedPertanyaan.id}/reply?year=${year}&semester=${semester}`,
        {
          method: "POST",
          body: JSON.stringify({ pesan: replyText }),
        },
      );

      const newReply: Reply = {
        pesan: replyText,
        role: "admin",
        nama: "Admin",
        created_at: new Date().toISOString(),
      };

      // Optimistically update the list
      setData((prev) =>
        prev.map((item) =>
          item.id === selectedPertanyaan.id
            ? {
                ...item,
                replies: [...(item.replies || []), newReply],
              }
            : item,
        ),
      );

      // Update the currently opened dialog data so the new chat bubble appears
      setSelectedPertanyaan((prev) =>
        prev
          ? {
              ...prev,
              replies: [...(prev.replies || []), newReply],
            }
          : null,
      );

      toast.success("Jawaban berhasil dikirim ke dosen.");

      // Clear the text area, but DO NOT close the dialog
      setReplyText("");
    } catch (err: any) {
      toast.error(err.message || "Gagal mengirim balasan.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (replyText.trim() && !isSubmitting) {
        handleSubmitReply();
      }
    }
  };

  const handleToggleStatus = async (
    newStatus: "open" | "closed",
    id?: string,
  ) => {
    const targetId = id || selectedPertanyaan?.id;
    if (!targetId) return;

    setIsTogglingStatus(true);
    try {
      await apiClient(
        `${import.meta.env.VITE_API_BASE_URL}/api/v1/admin/pengaduan/${targetId}/status?year=${year}&semester=${semester}`,
        {
          method: "PUT",
          body: JSON.stringify({
            status: newStatus === "open" ? "in_progress" : "closed",
          }),
        },
      );

      setData((prev) =>
        prev.map((item) =>
          item.id === targetId ? { ...item, status: newStatus } : item,
        ),
      );

      if (selectedPertanyaan?.id === targetId) {
        setSelectedPertanyaan((prev) =>
          prev ? { ...prev, status: newStatus } : null,
        );
      }

      toast.success(
        `Status berhasil diubah menjadi ${newStatus === "closed" ? "Selesai" : "Terbuka"}`,
      );
    } catch (err: any) {
      toast.error(err.message || "Gagal mengubah status.");
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return (
      new Date(dateStr).toLocaleString("id-ID", {
        dateStyle: "medium",
        timeStyle: "short",
      }) + " WIB"
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-800">
          Pertanyaan & Masukan
        </h2>
        <p className="text-muted-foreground">
          Kelola, tinjau, dan balas pertanyaan atau masukan yang diajukan oleh
          dosen.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3 border-b border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-lg">Daftar Pengajuan</CardTitle>
              <CardDescription>
                Daftar pertanyaan dan masukan yang telah diajukan.
              </CardDescription>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Cari judul/masukan..."
                  className="pl-9 bg-slate-50 border-slate-200 focus-visible:ring-primary"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <Select
                value={statusFilter}
                onValueChange={(val) => {
                  setStatusFilter(val);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-35 bg-slate-50 border-slate-200">
                  <SelectValue placeholder="Semua Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-sm bg-slate-300" />
                      Semua Status
                    </div>
                  </SelectItem>
                  <SelectItem value="open">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-sm bg-amber-400" />
                      Terbuka
                    </div>
                  </SelectItem>
                  <SelectItem value="closed">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-sm bg-emerald-400" />
                      Selesai
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              {(search !== "" || statusFilter !== "all") && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("all");
                    setPage(1);
                  }}
                  className="px-3 text-slate-500 hover:text-slate-900"
                >
                  <X className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50/50">
              <TableRow>
                <TableHead className="w-50">Dosen</TableHead>
                <TableHead>Judul Masukan</TableHead>
                <TableHead className="w-45">Tanggal</TableHead>
                <TableHead className="w-25 text-center">Status</TableHead>
                <TableHead className="w-30 text-center">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
                    <p className="text-sm text-slate-500 mt-2">
                      Memuat data...
                    </p>
                  </TableCell>
                </TableRow>
              ) : data.length > 0 ? (
                data.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <p className="font-semibold text-slate-700">
                        {item.nama}
                      </p>
                      <p className="text-xs text-slate-500">{item.niu}</p>
                    </TableCell>
                    <TableCell className="max-w-50 sm:max-w-75">
                      <p
                        className="font-medium text-slate-800 line-clamp-2 wrap-break-word"
                        title={item.judul}
                      >
                        {item.judul}
                      </p>
                      <div
                        className="text-sm text-slate-500 line-clamp-1 wrap-break-word mt-0.5"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(item.isi),
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-xs text-slate-500">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(item.created_at)}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        variant={
                          item.status === "closed" ? "default" : "secondary"
                        }
                        className={
                          item.status === "closed"
                            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-emerald-200"
                            : "bg-amber-100 text-amber-700 hover:bg-amber-100 border-amber-200"
                        }
                      >
                        {item.status === "closed" ? "Selesai" : "Terbuka"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          size="sm"
                          variant={
                            item.status !== "closed" ? "default" : "outline"
                          }
                          className={
                            item.status !== "closed"
                              ? "bg-primary flex-1"
                              : "flex-1"
                          }
                          onClick={() => handleOpenReply(item)}
                        >
                          {item.status !== "closed" ? "Jawab" : "Detail"}
                        </Button>
                        {item.status !== "closed" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => setConfirmCloseId(item.id)}
                            disabled={isTogglingStatus}
                          >
                            Selesaikan
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-12 text-slate-500"
                  >
                    Tidak ada data yang ditemukan.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <div className="flex flex-col sm:flex-row items-center justify-between px-2 py-4 mt-4 border-t border-slate-100 gap-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-500">
                Menampilkan {data.length} data. Baris per halaman:
              </span>
              <Select
                value={limit.toString()}
                onValueChange={(val) => {
                  setLimit(parseInt(val, 10));
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-8 w-17.5">
                  <SelectValue placeholder={limit.toString()} />
                </SelectTrigger>
                <SelectContent side="top">
                  {[10, 20, 50, 100].map((pageSize) => (
                    <SelectItem key={pageSize} value={pageSize.toString()}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Sebelumnya
              </Button>
              <div className="text-sm font-medium px-4">Halaman {page}</div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={!isNext}
              >
                Selanjutnya
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] p-0 flex flex-col overflow-hidden gap-0 bg-slate-100">
          <DialogHeader className="px-6 py-4 border-b border-slate-200 bg-white shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <MessageSquareWarning className="w-5 h-5 text-primary" />
              Detail Pertanyaan & Masukan
            </DialogTitle>
            <DialogDescription>
              Baca masukan dosen dan berikan jawaban yang sesuai.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-4 custom-scrollbar w-full">
            {selectedPertanyaan && (
              <div className="flex flex-col gap-5 w-full min-w-0">
                {/* Info Pengirim */}
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm w-full min-w-0">
                  <div className="flex justify-between items-start mb-3 gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg text-slate-800 leading-tight wrap-break-word">
                        {selectedPertanyaan.judul}
                      </h3>
                      <p className="text-sm text-slate-600 mt-1 wrap-break-word">
                        Dari: <strong>{selectedPertanyaan.nama}</strong> (
                        {selectedPertanyaan.niu})
                      </p>
                    </div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1.5 shrink-0 bg-slate-100 px-2.5 py-1 rounded-full font-medium">
                      <Clock className="w-3.5 h-3.5" />
                      {formatDate(selectedPertanyaan.created_at)}
                    </div>
                  </div>
                  <div
                    className="bg-slate-50/50 p-3 rounded-lg border border-slate-100 text-sm text-slate-700 prose prose-sm max-w-none wrap-break-word w-full overflow-hidden [&_img]:max-w-full [&_img]:h-auto [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_table]:max-w-full [&_table]:block [&_table]:overflow-x-auto"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(selectedPertanyaan.isi),
                    }}
                  />

                  {/* Gambar/Lampiran */}
                  {selectedPertanyaan.images &&
                    selectedPertanyaan.images.length > 0 && (
                      <div className="mt-4">
                        <h5 className="text-xs font-semibold text-slate-500 mb-2">
                          LAMPIRAN:
                        </h5>
                        <div className="flex flex-wrap gap-2">
                          {selectedPertanyaan.images.map((imgUrl, i) => {
                            const isPdf = imgUrl.toLowerCase().endsWith(".pdf");
                            const fileName =
                              imgUrl.split("/").pop() || "Lampiran";
                            return (
                              <a
                                key={i}
                                href={`${imgUrl}`}
                                target="_blank"
                                rel="noreferrer"
                                className="w-20 h-20 rounded-md border border-slate-200 overflow-hidden bg-slate-100 hover:opacity-80 transition-opacity flex flex-col items-center justify-center"
                                title={fileName}
                              >
                                {isPdf ? (
                                  <div className="flex flex-col items-center justify-center h-full w-full bg-slate-50 text-slate-400 p-2 text-center">
                                    <FileText
                                      size={24}
                                      className="mb-1 text-slate-500"
                                    />
                                    <span className="text-[9px] leading-tight font-medium truncate w-full px-1">
                                      PDF
                                    </span>
                                  </div>
                                ) : (
                                  <img
                                    src={`${imgUrl}`}
                                    alt="Lampiran"
                                    className="w-full h-full object-cover"
                                  />
                                )}
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                </div>

                {/* Balasan Sebelumnya */}
                {selectedPertanyaan.replies &&
                  selectedPertanyaan.replies.length > 0 && (
                    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm w-full min-w-0">
                      <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-4 border-b border-slate-100 pb-2">
                        <MessageSquareWarning className="w-4 h-4 text-slate-400" />
                        Riwayat Balasan
                      </h4>
                      <div className="flex flex-col gap-3">
                        {selectedPertanyaan.replies.map((reply, idx) => (
                          <div
                            key={idx}
                            className={`p-3 rounded-lg border text-sm relative min-w-0 w-full overflow-hidden ${
                              reply.role === "admin"
                                ? "bg-primary/5 border-primary/10 ml-4"
                                : "bg-slate-50 border-slate-200 mr-4"
                            }`}
                          >
                            <div className="flex justify-between items-start mb-1.5 gap-2">
                              <span className="font-semibold text-slate-800 text-xs flex-1 min-w-0 wrap-break-word">
                                {reply.nama}
                              </span>
                              <span className="text-[10px] text-slate-400 whitespace-nowrap shrink-0">
                                {formatDate(reply.created_at)}
                              </span>
                            </div>
                            <div className="text-slate-700 whitespace-pre-wrap wrap-break-word min-w-0 w-full">
                              {reply.pesan}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Form Balasan */}
                {selectedPertanyaan.status !== "closed" && (
                  <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm w-full min-w-0">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                        Tulis Jawaban
                      </h4>
                    </div>

                    <Textarea
                      placeholder="Ketikkan balasan untuk dosen terkait (Tekan Enter untuk kirim, Shift+Enter untuk baris baru)..."
                      className="min-h-32 resize-y focus-visible:ring-primary border-slate-200 bg-slate-50"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 mb-1 border-t border-slate-200 bg-slate-50/80 backdrop-blur-sm shrink-0 flex items-center justify-between sm:justify-between w-full">
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={isSubmitting || isTogglingStatus}
            >
              Tutup
            </Button>
            <div className="flex gap-2">
              {selectedPertanyaan?.status !== "closed" && (
                <>
                  <Button
                    variant="destructive"
                    onClick={() =>
                      setConfirmCloseId(selectedPertanyaan?.id || null)
                    }
                    disabled={isSubmitting || isTogglingStatus}
                  >
                    {isTogglingStatus && (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    )}
                    Tandai Selesai
                  </Button>
                  <Button
                    onClick={handleSubmitReply}
                    disabled={isSubmitting || isTogglingStatus}
                  >
                    {isSubmitting && (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    )}
                    Kirim Balasan
                  </Button>
                </>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmCloseId}
        onOpenChange={(open) => !open && setConfirmCloseId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmasi Selesai</AlertDialogTitle>
            <AlertDialogDescription>
              Apakah Anda yakin ingin menyelesaikan/menutup tiket pengajuan ini?
              Tiket yang sudah selesai tidak dapat dibuka kembali atau dibalas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isTogglingStatus}>
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isTogglingStatus}
              onClick={(e) => {
                e.preventDefault();
                if (confirmCloseId) {
                  handleToggleStatus("closed", confirmCloseId).finally(() => {
                    setConfirmCloseId(null);
                  });
                }
              }}
            >
              {isTogglingStatus ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Ya, Selesaikan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
